import { RichText } from "@punks/core"
import React from "react"
import { CustomCol, CustomRow } from "../../../../ui/spacings"
import { useStores } from "../../hooks"
import { StoreDocumentV2 } from "../../../../api/backend/backend-api"
import CustomSelect from "../../../../ui/atoms/CustomSelect"
import { FiPhone } from "react-icons/fi"
import { toTitleCase } from "../../../../utils/strings"
import Typography from "../../../../ui/atoms/Typography"
import { TwRichTextField } from "../../../../root/punks/core/fields/RichTextField"
import StoreMap from "../StoreMap"
import { EditorialLabel } from "../../../contents"

export interface StoreLocatorProps {
  title: RichText
}

const StoreLocator = ({ title }: StoreLocatorProps) => {
  const { data } = useStores({
    includeHidden: false,
  })
  const [store, setStore] = React.useState<StoreDocumentV2>()

  React.useEffect(() => {
    if (data.length > 0) {
      setStore(data[0] as StoreDocumentV2)
    }
  }, [data])

  return (
    <CustomRow spacing>
      <CustomCol xs={12} sm={6}>
        <div className="flex flex-col justify-between h-full">
          <div>
            <TwRichTextField value={title} variant="h4" />
            <div className="my-6">
              <CustomSelect
                values={data.map((x) => ({
                  id: x.id,
                  label: `${x.name.toUpperCase()}, ${toTitleCase(
                    x.address.street
                  )}`,
                }))}
                value={store?.id ?? ""}
                onChange={(id: string) =>
                  setStore(data.find((x) => x.id === id) as StoreDocumentV2)
                }
                size="medium"
                className="w-full sm:w-auto"
              />
            </div>
          </div>
          {store && (
            <div className="mt-4">
              <div className="mb-4">
                <Typography variant="subtitle2">
                  {store.company ?? ""} {store.name}
                </Typography>
              </div>
              <Typography variant="subtitle2" weight="normal">
                {store.address.street}
              </Typography>
              <div className="mt-4">
                <Typography variant="subtitle2" weight="normal">
                  {store.address.cap} - {store.address.city}
                  {store.address.province !== store.address.city
                    ? ` (${store.address.province})`
                    : ""}
                </Typography>
              </div>
              {store.contacts.phone && (
                <a href={`tel:+39${store.contacts.phone}`}>
                  <div className="flex mt-4">
                    <FiPhone fontSize={16} className="mr-2" />
                    <Typography variant="subtitle2" weight="normal">
                      {store.contacts.phone}
                    </Typography>
                  </div>
                </a>
              )}
              <CustomRow className="mt-4">
                {store.businessHours && store.businessHours.length > 0 && (
                  <CustomCol xs={12} sm={6}>
                    <div className="mt-6">
                      <EditorialLabel
                        scope="stores"
                        id="open-hours"
                        variant="subtitle2"
                      />
                    </div>
                    {store.businessHours.map((x, index) => (
                      <Typography
                        key={index}
                        variant="body1"
                        component="p"
                        className="my-1"
                      >
                        <span className="mr-2 ">{x.day}:</span>
                        {x.hours}
                      </Typography>
                    ))}
                  </CustomCol>
                )}
                {store.services && store.services.length > 0 && (
                  <CustomCol xs={12} sm={6}>
                    <EditorialLabel
                      scope="stores"
                      id="store-services"
                      variant="subtitle2"
                      className="mt-6"
                    />
                    {store.services.map((x, index) => (
                      <Typography
                        key={index}
                        variant="body1"
                        component="p"
                        className="my-1"
                      >
                        {x.name}
                      </Typography>
                    ))}
                  </CustomCol>
                )}
              </CustomRow>
            </div>
          )}
        </div>
      </CustomCol>
      <CustomCol xs={12} sm={6} className="mt-6 sm:mt-0">
        {store && <StoreMap store={store} />}
      </CustomCol>
    </CustomRow>
  )
}

export default StoreLocator
